<script setup lang="ts">
import { ref, watch, computed, onMounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { defineRule } from "vee-validate";
import { storeToRefs } from "pinia";
import SelectStoreDeposit from "@/components/menberFunction/depositPage/SelectStoreDeposit.vue";
import { useMemberService } from "@/stores/memberFunction/memberService";
import { useLogin } from "@/stores/login";
import { useCounterStore } from "@/stores/counter";
import type { MemberRechargePage2ListData } from "@/assets/API/APItype";

import triangle_icon from "@image/memberFunction/common/triangle_icon.png";
import 文字框_長_A from "@image/memberFunction/文字框/文字框_長_A.png";
import 驚嘆號icon from "@image/memberFunction/11/驚嘆號icon.png";
import PlatformBtn from "@image/memberFunction/01/my_btn.png";

interface DepositFormData {
  payCount: number;
  aTunnel: string;
}
interface DepositPostData {
  nId: string;
  sPay_name: string;
  sNickname: string;
  nMax: string;
  nMin: string;
  sGids: string;
  nMoney: string;
  t: string;
  a: string;
  aTunnel: string;
  postUrl: string;
}
const { t } = useI18n();
const memberService = useMemberService();
const store = useCounterStore();
const mode = import.meta.env.VITE_CONFIG_MODE;
const props = defineProps<{
  Info: MemberRechargePage2ListData[];
}>();
const DepositForm = ref<any>(null);
const selectedIndex = ref<number | null>(null);
const selectedInfo = computed(() => selectedIndex.value !== null ? props.Info[selectedIndex.value] : null);
const paymentCount = ref(0);

// 快捷金额选项 - 使用动态数据
const countList = computed(() => {
  if (!selectedInfo.value?.aPayment?.aTopUpMoneyBtn) {
    return [1100, 3100, 5100, 10100, 20100, 50100]; // 默认值
  }
  return selectedInfo.value.aPayment.aTopUpMoneyBtn.map(item => Number(item));
});

// 支付渠道 aTunnel
const PayATunnel = computed(() => {
  if (selectedInfo.value === null) return [];
  const payment = selectedInfo.value.aPayment[parseInt(selectedInfo.value.aPayment.nId)];

  if (payment && typeof payment === "object" && "aTunnel" in payment) {
    return payment.aTunnel;
  }
  return [];
});

// 監視payCount變化時要在底下顯示最大最小值
watch(
  () => paymentCount.value,
  (newValue) => {
    const numValue = Number(newValue);
    if (!selectedInfo.value) return;
    const { nMax, nMin } = selectedInfo.value.aPayment;

    // 检查最大最小值
    if (numValue > Number(nMax)) {
      DepositForm.value.setFieldError("payCount", t('MemberFunction.Deposit.MaxAmount', { nMax }));
      return;
    } else if (numValue < Number(nMin)) {
      DepositForm.value.setFieldError("payCount", t('MemberFunction.Deposit.MinAmount', { nMin }));
      return;
    }

    // 检查尾数限制
    const stintError = validateMoneyStint(numValue);
    if (stintError) {
      DepositForm.value.setFieldError("payCount", stintError);
    } else {
      DepositForm.value.setFieldError("payCount", "");
    }
  },
);

function submit(VForm: DepositFormData) {
  console.log(VForm.aTunnel);
  if (!selectedInfo.value) return;
  const { nId, sPay_name, sNickname, nMax, nMin, sGids, t, a, postUrl } =
    selectedInfo.value.aPayment;
  const { payCount, aTunnel } = VForm;
  console.log('aTunnel:', aTunnel);
  const postData: DepositPostData = {
    nId,
    sPay_name,
    sNickname,
    nMax,
    nMin,
    sGids,
    t: t.toString(),
    a,
    nMoney: payCount.toString(),
    aTunnel: aTunnel, // 使用表單中的動態值
    postUrl,
  };
  DepositForm.value.resetForm();
  memberService.getMemberRecharge2(postData);
}

// 验证金额尾数限制
function validateMoneyStint(value: number): string | null {
  if (!selectedInfo.value?.aPayment?.aTopUpMoneyStint) {
    return null; // 没有限制规则
  }

  const stint = selectedInfo.value.aPayment.aTopUpMoneyStint;
  const valueStr = value.toString();
  const len = valueStr.length;

  // 获取各位数字
  const singleDigit = len >= 1 ? valueStr[len - 1] : '0';
  const tenDigit = len >= 2 ? valueStr[len - 2] : '0';
  const hundredDigit = len >= 3 ? valueStr[len - 3] : '0';

  // 验证个位数
  if (stint.sSingleDigits !== '*') {
    if (stint.sSingleDigits === '-0' && singleDigit === '0') {
      return t('MemberFunction.Deposit.SingleDigitCannotBeZero');
    } else if (stint.sSingleDigits.startsWith('+')) {
      const requiredDigit = stint.sSingleDigits.substring(1);
      if (singleDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.SingleDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  // 验证十位数
  if (stint.sTens !== '*') {
    if (stint.sTens === '-0' && tenDigit === '0') {
      return t('MemberFunction.Deposit.TenDigitCannotBeZero');
    } else if (stint.sTens.startsWith('+')) {
      const requiredDigit = stint.sTens.substring(1);
      if (tenDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.TenDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  // 验证百位数
  if (stint.sHundreds !== '*') {
    if (stint.sHundreds === '-0' && hundredDigit === '0') {
      return t('MemberFunction.Deposit.HundredDigitCannotBeZero');
    } else if (stint.sHundreds.startsWith('+')) {
      const requiredDigit = stint.sHundreds.substring(1);
      if (hundredDigit !== requiredDigit) {
        return t('MemberFunction.Deposit.HundredDigitMustBe', { digit: requiredDigit });
      }
    }
  }

  return null;
}

// 判斷是否為百元整數（個位數與十位數必須為 0） - 保留用于向后兼容
function isHundredMultiple(value: number) {
  // 檢查是否為 100 的倍數
  return value % 100 === 0;
}

defineRule('unit_digit_zero', (value: number | string) => {
  const num = Number(value);
  // 使用动态验证函数
  const error = validateMoneyStint(num);
  if (error) {
    return error;
  }
  return true;
});

// 快捷金额设置函数
function setQuickAmount(amount: number) {
  if (!selectedInfo.value) return;

  // 直接设置表单值，让验证机制处理错误提示
  DepositForm.value.setFieldValue('payCount', amount);
  paymentCount.value = amount;
}

// 添加千分位逗号函数
function addCommas(str: string) {
  return store.addCommas(str);
}

// 清除金额函数
function clearAmount() {
  DepositForm.value.setFieldValue('payCount', '');
  paymentCount.value = 0;
}

// 选择平台函数
function selectPlatform(index: number) {
  selectedIndex.value = index;

  // 動態設置 aTunnel 值
  const selectedPayment = props.Info[index];
  console.log('selectedPayment:', selectedPayment);
  if (selectedPayment && selectedPayment.aPayment) {
    const paymentData = selectedPayment.aPayment[selectedPayment.aPayment.nId as keyof typeof selectedPayment.aPayment];
    if (paymentData && typeof paymentData === 'object' && 'aTunnel' in paymentData && paymentData.aTunnel) {
      // 取 aTunnel 物件的第一個 key 值
      const aTunnelKeys = Object.keys(paymentData.aTunnel);
      const aTunnelValue = aTunnelKeys.length > 0 ? aTunnelKeys[0] : '1';
      console.log('aTunnelValue:', aTunnelValue);
      DepositForm.value?.setFieldValue('aTunnel', aTunnelValue);
    } else {
      DepositForm.value?.setFieldValue('aTunnel', '1');
    }
  }
}

// 监听Info数据变化，自动选择第一个选项
watch(
  () => props.Info,
  (newInfo) => {
    console.log('props.Info changed:', newInfo);
    if (newInfo && newInfo.length > 0 && selectedIndex.value === null) {
      selectPlatform(0);
    }
  },
  { immediate: true }
);

onMounted(() => {
  nextTick(() => {
    if (props.Info && props.Info.length > 0) {
      selectPlatform(0);
    }
  });
});

</script>

<template>
  <VForm @submit="submit" ref="DepositForm" class="py-[2em] px-[9vw]">
    <div class="text-primary mb-[3dvh]">
      <label for="payment" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-12">
          {{ $t("MemberFunction.Deposit.platform") }} <span class="text-red-600 text-[12px]">({{
            t("MemberFunction.Deposit.SelectDepositMethod") }})</span>
        </p>
        <div class="col-span-12 mt-2">
          <div class="grid grid-cols-2 md:grid-cols-3 gap-2">
            <button type="button" v-for="(item, idx) in props.Info" :key="item.nId" @click="selectPlatform(idx)"
              class="relative" :class="{ 'opacity-50': selectedIndex !== idx }">
              <img v-img-src="PlatformBtn" alt="" class="w-full h-full" />
              <p class="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 text-white text-[14px] font-bold text-center whitespace-nowrap"
                v-html="item.aPayment.sPay_name.replace(/\/n/g, '<br>')">
              </p>
            </button>
          </div>
        </div>
      </label>
      <!-- 隐藏的aTunnel字段，動態值 -->
      <VField id="aTunnel" :label="$t('MemberFunction.Deposit.PaymentMethod')" name="aTunnel" type="hidden"
        :rules="{ required: true }" class="hidden" />
      <label for="payCount" class="grid grid-cols-12 text-[14px] md:text-[16px] mt-[16px] md:mt-[3dvh]">
        <p class="mr-[1em] col-span-4">
          {{ $t("MemberFunction.Deposit.DepositAmount") }}
        </p>
        <div class="relative inline-block col-span-8">
          <div class="h-[26px]">
            <img v-img-src="文字框_長_A" alt="" class="h-[26px] w-full" />
          </div>
          <VField class="col-span-1 absolute h-[26px] top-0 rounded-md bg-transparent w-full px-2" id="payCount"
            :label="$t('MemberFunction.Deposit.DepositAmount')" name="payCount" type="number"
            @input="paymentCount = Number($event.target.value)" :max="selectedInfo?.aPayment.nMax"
            :min="selectedInfo?.aPayment.nMin" :rules="{
              required: true, min_value: selectedInfo?.aPayment.nMin,
              max_value: selectedInfo?.aPayment.nMax,
              unit_digit_zero: true
            }" />
          <p class="text-primary text-[12px] mr-[6px]">
            {{ $t("MemberFunction.Deposit.DepositAmount") }}: {{ selectedInfo?.aPayment.nMin }} ~
            {{ selectedInfo?.aPayment.nMax }} {{ $t("MemberFunction.Withdraw.TWD") }}
          </p>
          <ErrorMessage name="payCount" v-slot="{ message }">
            <p class="text-yellow-500 font-bold text-[12px] mr-[6px]">
              {{
                message.includes("必填")
                  ? $t("MemberFunction.Deposit.Require")
                  : message
              }}
            </p>
          </ErrorMessage>
        </div>
      </label>
    </div>

    <!-- 內扣手續費提示 -->
    <div v-if="selectedInfo?.aPayment.sInsideFeeTip" class="w-full pb-[3dvh] flex">
      <p class="text-red-600 mr-1">※</p>
      <p>
        {{ selectedInfo?.aPayment.sInsideFeeTip }}
      </p>
    </div>

    <!-- 快捷金额按钮 -->
    <div class="mb-[3dvh]">
      <!-- 手机版：3x2 网格布局 -->
      <div class="grid grid-cols-3 gap-2 md:hidden">
        <button v-for="count in countList" :key="'ATMCountList' + count" type="button"
          class="btn-y-border w-full inline-block py-1 px-1" @click="setQuickAmount(count)">
          <p class="text-[12px] font-bold text-secondary">
            {{ addCommas(count.toString()) }}
          </p>
        </button>
        <button type="button" class="btn-y-border w-full inline-block py-1 px-1" @click="clearAmount">
          <p class="text-[12px] font-bold text-secondary">
            {{ $t("MemberFunction.Deposit.Clear") }}
          </p>
        </button>
      </div>
      <!-- 桌面版：水平布局 -->
      <ul class="hidden md:flex justify-between items-center">
        <li v-for="count in countList" :key="'ATMCountList' + count" class="mr-[3px] w-full">
          <button type="button" class="btn-y-border w-full inline-block px-1" @click="setQuickAmount(count)">
            <p class="text-[14px] font-bold text-secondary">
              {{ addCommas(count.toString()) }}
            </p>
          </button>
        </li>
        <li class="w-full">
          <button type="button" class="btn-y-border w-full inline-block" @click="clearAmount">
            <p class="text-[14px] font-bold text-secondary">
              {{ $t("MemberFunction.Deposit.Clear") }}
            </p>
          </button>
        </li>
      </ul>
    </div>

    <div class="md:static flex flex-col justify-center items-center">
      <div class="w-full flex gap-[10px] justify-center flex-col">
        <div class="w-full">
          <button type="submit" class="btn-f-border">
            <p class="font-bold text-secondary py-[.5em] md:py-[1em]">
              {{ $t("MemberFunction.Deposit.Next") }}
            </p>
          </button>
        </div>
      </div>
      <div class="mt-[3dvh]">
        <div class="flex justify-center items-center leading-[36px]">
          <img v-img-src="驚嘆號icon" alt="" class="w-[16px] mr-1" />
          <span class="text-red-600">
            {{ $t("MemberFunction.Deposit.Notice") }}
          </span>
        </div>
        <div class="text-primary text-[14px] md:text-[16px] leading-[20px]">
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.ATMNoticeList1") }}
            </p>
          </div>
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>{{ $t("MemberFunction.Deposit.ATMNoticeList2") }}</p>
          </div>
          <div class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.ATMNoticeList3") }}
            </p>
          </div>
          <div v-if="mode === 'LLU'" class="mb-[1vh] flex">
            <p class="text-red-600 mr-1">※</p>
            <p>
              {{ $t("MemberFunction.Deposit.ATMNoticeList4") }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </VForm>
</template>

<style scoped>
.btn-f-border {
  width: 100%;
  border-style: solid;
  border-width: 1px;
  -moz-border-image: url("@image/memberFunction/07/login_big_btn.png") 1;
  -webkit-border-image: url("@image/memberFunction/07/login_big_btn.png") 1;
  -o-border-image: url("@image/memberFunction/07/login_big_btn.png") 1;
  border-image: url("@image/memberFunction/07/login_big_btn.png") 1 fill;
}
</style>
